import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

const commentMentionSerializer = Serializers.object({
  user: Serializers.string,
  tagPosition: Serializers.integer,
  tagLength: Serializers.integer,
});

export const ticketStatuses = ["open", "resolved", "closed"] as const;
export type TicketStatus = (typeof ticketStatuses)[number];

export const priorities = ["low", "medium", "high", "urgent"] as const;
export type Priority = (typeof priorities)[number];

export const ticketCategories = ["complaint", "question", "unofficialComplaint"] as const;
export type TicketCategory = (typeof ticketCategories)[number];

const assigneeSummarySerializer = Serializers.object({
  email: Serializers.string,
});

export const messageAttachmentSummarySerializer = Serializers.object({
  id: uuidSerializer,
  fileName: Serializers.string,
  fileType: Serializers.string,
});

export type MessageAttachmentSummaryDto = SerializerValue<typeof messageAttachmentSummarySerializer>;

export const messageAttachmentPayloadSerializer = Serializers.object({
  fileName: Serializers.string,
  fileType: Serializers.string,
  contentBase64: Serializers.string,
});

export type MessageAttachmentPayloadDto = SerializerValue<typeof messageAttachmentPayloadSerializer>;

export const messageSerializer = Serializers.object({
  id: uuidSerializer,
  ticketId: uuidSerializer,
  body: Serializers.string,
  fromAgent: Serializers.boolean,
  authorEmail: Serializers.string.nullable(),
  imapMsgId: Serializers.string.nullable(),
  createdAt: Serializers.datetime,
  attachments: messageAttachmentSummarySerializer.array(),
});

export type MessageDto = SerializerValue<typeof messageSerializer>;

export const ticketProductSerializer = Serializers.object({
  id: uuidSerializer,
  productName: Serializers.string,
  serialNumber: Serializers.string.nullable(),
});

export type TicketProductDto = SerializerValue<typeof ticketProductSerializer>;

export const ticketListItemSerializer = Serializers.object({
  id: uuidSerializer,
  ticketNumber: Serializers.string,
  subject: Serializers.string,
  status: Serializers.stringOneOf(...ticketStatuses),
  priority: Serializers.stringOneOf(...priorities).nullable(),
  category: Serializers.stringOneOf(...ticketCategories).nullable(),
  tags: Serializers.string.array(),
  reporterName: Serializers.string,
  reporterEmail: Serializers.string,
  sourceMailbox: Serializers.string.nullable(),
  assignees: assigneeSummarySerializer.array(),
  lastReplyAt: Serializers.datetime.nullable(),
  createdAt: Serializers.datetime,
  isRead: Serializers.boolean,
});

export type TicketListItemDto = SerializerValue<typeof ticketListItemSerializer>;

const ticketCommentSerializer = Serializers.object({
  id: uuidSerializer,
  body: Serializers.string,
  authorEmail: Serializers.string.nullable(),
  mentions: commentMentionSerializer.array(),
  createdAt: Serializers.datetime,
});

export const ticketHistoryEntryTypes = ["ticketCreated", "statusChanged"] as const;
export type TicketHistoryEntryType = (typeof ticketHistoryEntryTypes)[number];

const ticketHistoryEntrySerializer = Serializers.object({
  id: uuidSerializer,
  entryType: Serializers.stringOneOf(...ticketHistoryEntryTypes),
  changedByEmail: Serializers.string.nullable(),
  oldValue: Serializers.string.nullable(),
  newValue: Serializers.string.nullable(),
  createdAt: Serializers.datetime,
});

export type TicketHistoryEntry = SerializerValue<typeof ticketHistoryEntrySerializer>;

export const ticketDetailSerializer = Serializers.object({
  id: uuidSerializer,
  ticketNumber: Serializers.string,
  subject: Serializers.string,
  status: Serializers.stringOneOf(...ticketStatuses),
  priority: Serializers.stringOneOf(...priorities).nullable(),
  category: Serializers.stringOneOf(...ticketCategories).nullable(),
  tags: Serializers.string.array(),
  reporterName: Serializers.string,
  reporterEmail: Serializers.string,
  assignees: assigneeSummarySerializer.array(),
  messages: messageSerializer.array(),
  notes: ticketCommentSerializer.array(),
  history: ticketHistoryEntrySerializer.array(),
  threadId: Serializers.string.nullable(),
  sourceMailbox: Serializers.string.nullable(),
  invoiceNumber: Serializers.string.nullable(),
  customerUuids: uuidSerializer.array(),
  products: ticketProductSerializer.array(),
  createdAt: Serializers.datetime,
  updatedAt: Serializers.datetime,
});

export type TicketDetailDto = SerializerValue<typeof ticketDetailSerializer>;

const ticketsQueryQsSerializer = Serializers.object({
  offset: Serializers.integer.optional(),
  limit: Serializers.integer.optional(),
  searchText: Serializers.string.optional(),
  status: Serializers.stringOneOf(...ticketStatuses)
    .array()
    .asJsonString()
    .optional(),
  priority: Serializers.stringOneOf(...priorities)
    .array()
    .asJsonString()
    .optional(),
  category: Serializers.stringOneOf(...ticketCategories)
    .array()
    .asJsonString()
    .optional(),
  tags: Serializers.string.array().asJsonString().optional(),
  sort: Serializers.string.optional(),
  sortOrder: Serializers.stringOneOf("asc", "desc").optional(),
  myTickets: Serializers.boolean.optional(),
  isRead: Serializers.boolean.optional(),
  contactEmail: Serializers.string.optional(),
  customerUuid: uuidSerializer.optional(),
});

export type TicketsQueryQs = SerializerValue<typeof ticketsQueryQsSerializer>;

const ticketsListValueSerializer = Serializers.object({
  items: ticketListItemSerializer.array(),
  count: Serializers.integer,
});
export type TicketsListValue = SerializerValue<typeof ticketsListValueSerializer>;

export const updateTicketBodySerializer = Serializers.object({
  subject: Serializers.string.nullable().optional(),
  status: Serializers.stringOneOf(...ticketStatuses).nullable().optional(),
  priority: Serializers.stringOneOf(...priorities).nullable().optional(),
  category: Serializers.stringOneOf(...ticketCategories).nullable().optional(),
  tags: Serializers.string.array().nullable().optional(),
  invoiceNumber: Serializers.string.nullable().optional(),
});

export type UpdateTicketBody = SerializerValue<typeof updateTicketBodySerializer>;
export const updateTicketAssigneesBodySerializer = Serializers.object({
  assigneeEmails: Serializers.string.array().nullable(),
});

export type UpdateTicketAssigneesBody = SerializerValue<typeof updateTicketAssigneesBodySerializer>;

export const ticketActions = {
  ticketsList: HttpAction({
    path: "/tickets",
    method: "get",
    qsSerializer: ticketsQueryQsSerializer,
    valueSerializer: ticketsListValueSerializer,
    requiresAuth: true,
  }),

  createTicket: HttpAction({
    path: "/tickets",
    method: "post",
    bodySerializer: Serializers.object({
      subject: Serializers.string,
      message: Serializers.string.nullable(),
      reporterEmail: Serializers.email.nullable(),
      reporterName: Serializers.string,
      sourceMailbox: Serializers.string.nullable(),
      invoiceNumber: Serializers.string.nullable(),
      status: Serializers.stringOneOf(...ticketStatuses),
      priority: Serializers.stringOneOf(...priorities).nullable(),
      category: Serializers.stringOneOf(...ticketCategories).nullable(),
      tags: Serializers.string.array().nullable(),
      assigneeEmails: Serializers.string.array().nullable(),
      attachments: messageAttachmentPayloadSerializer.array().nullable(),
      products: Serializers.object({
        productName: Serializers.string,
        serialNumber: Serializers.string.nullable(),
      })
        .array()
        .nullable(),
    }),
    valueSerializer: ticketDetailSerializer,
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  getTicket: HttpAction({
    path: "/tickets/:id",
    method: "get",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    valueSerializer: ticketDetailSerializer,
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  updateTicket: HttpAction({
    path: "/tickets/:id",
    method: "patch",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    bodySerializer: updateTicketBodySerializer,
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  updateTicketAssignees: HttpAction({
    path: "/tickets/:id/assignees",
    method: "put",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    bodySerializer: updateTicketAssigneesBodySerializer,
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  addTicketMessage: HttpAction({
    path: "/tickets/:id/messages",
    method: "post",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    bodySerializer: Serializers.object({
      body: Serializers.string,
      fromAgent: Serializers.boolean,
      resolve: Serializers.boolean.nullable(),
    }),
    valueSerializer: messageSerializer,
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  getTicketMessageAttachment: HttpAction({
    path: "/tickets/:id/messages/:messageId/attachments/:attachmentId",
    method: "get",
    paramsSerializer: Serializers.object({
      id: uuidSerializer,
      messageId: uuidSerializer,
      attachmentId: uuidSerializer,
    }),
    valueSerializer: messageAttachmentPayloadSerializer,
    errorSerializer: Serializers.stringOneOf("TicketNotFound", "AttachmentNotFound"),
    requiresAuth: true,
  }),

  mergeTickets: HttpAction({
    path: "/tickets/:id/merge",
    method: "post",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    bodySerializer: Serializers.object({
      targetTicketId: uuidSerializer,
    }),
    valueSerializer: ticketDetailSerializer,
    errorSerializer: Serializers.stringOneOf("TicketNotFound", "SameTicket"),
    requiresAuth: true,
  }),

  setTicketRead: HttpAction({
    path: "/tickets/:id/read",
    method: "post",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    bodySerializer: Serializers.object({
      isRead: Serializers.boolean,
    }),
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  addTicketCustomer: HttpAction({
    path: "/tickets/:id/customers",
    method: "post",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    bodySerializer: Serializers.object({ customerUuid: uuidSerializer }),
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  removeTicketCustomer: HttpAction({
    path: "/tickets/:id/customers/:customerUuid",
    method: "delete",
    paramsSerializer: Serializers.object({ id: uuidSerializer, customerUuid: uuidSerializer }),
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),

  updateTicketProducts: HttpAction({
    path: "/tickets/:id/products",
    method: "put",
    paramsSerializer: Serializers.object({ id: uuidSerializer }),
    bodySerializer: Serializers.object({
      products: Serializers.object({
        productName: Serializers.string,
        serialNumber: Serializers.string.nullable(),
      }).array(),
    }),
    valueSerializer: ticketProductSerializer.array(),
    errorSerializer: Serializers.stringOneOf("TicketNotFound"),
    requiresAuth: true,
  }),
};
